import { readApiError } from '@rr-crm/shared/api-errors';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../api/client';

/**
 * RIG-404 — Resources (the Sales Contractors SharePoint "Resource Hub").
 *
 * `useResourcesStatus` mirrors `useAssistantStatus` (RIG-389): the nav entry
 * and the route key off it so an environment without the hub configured
 * shows nothing rather than a page that always errors.
 */

const STATUS_QUERY_KEY = ['resources', 'status'] as const;

export interface ResourceItem {
  id: string;
  name: string;
  kind: 'file' | 'folder';
  sizeBytes: number;
  lastModifiedAt: string;
  mimeType: string | null;
  childCount: number | null;
}

export interface ResourceFolder {
  path: string;
  breadcrumbs: string[];
  folder: { id: string; name: string };
  items: ResourceItem[];
}

export class ResourcesError extends Error {
  public readonly status: number;
  public readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ResourcesError';
    this.status = status;
    this.code = code;
  }
}

export function useResourcesStatus() {
  return useQuery<boolean>({
    queryKey: STATUS_QUERY_KEY,
    queryFn: async () => {
      const resp = await apiFetch('/api/v1/resources/status');
      if (!resp.ok) return false;
      return ((await resp.json()) as { enabled: boolean }).enabled;
    },
    staleTime: Infinity,
  });
}

/** `false` while loading or on error, so the entry only appears once confirmed. */
export function useResourcesEnabled(): boolean {
  return useResourcesStatus().data ?? false;
}

/**
 * One folder of the hub, live from SharePoint. `path` is hub-relative
 * ('' = root). Short staleTime: a file added in SharePoint should show on
 * the next visit, but flipping between folders should not refetch each time.
 */
export function useResourceFolder(path: string) {
  return useQuery<ResourceFolder, ResourcesError>({
    queryKey: ['resources', 'browse', path],
    queryFn: async () => {
      const qs = path === '' ? '' : `?path=${encodeURIComponent(path)}`;
      const resp = await apiFetch(`/api/v1/resources/browse${qs}`);
      if (!resp.ok) {
        // QA 2026-10-01 F-28: the message is a plain sentence, never the body or status.
        const err = await readApiError(resp);
        throw new ResourcesError(resp.status, err.code, err.message);
      }
      return (await resp.json()) as ResourceFolder;
    },
    staleTime: 30_000,
    retry: (count, err) => count < 2 && err.status !== 404 && err.status !== 400 && err.status !== 503,
  });
}

export function resourceDownloadPath(itemId: string): string {
  return `/api/v1/resources/files/${encodeURIComponent(itemId)}/download`;
}
